/**
 * The one control an asset carries, in the 44px column at the end of a row and
 * in the top corner of a tile.
 *
 * It replaced two floating hover buttons on the tile and a single hover-only
 * bin on the list row. Both had the same problem: they appeared on hover, so
 * with a keyboard the file could be looked at but never acted on. This is a
 * button in the tab order, and the same three verbs sit behind it in both views
 * instead of each view offering its own subset.
 *
 * Copying the link is the one that had nowhere to live before. It is what a
 * media file is called everywhere outside this screen - in a page block, in a
 * letter, in a message to whoever asked for the picture - and it has to be an
 * absolute URL, because the library serves `/uploads/...` whenever R2 is off.
 */
import { Copy, Download, MoreHorizontal, Trash2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { absoluteMediaUrl, type MediaAsset } from "../../../lib/media";
import { Pop, useClosePops } from "../../shell/Pop";
import { useToast } from "../../ui";

export function MediaRowMenu({
  asset,
  onDelete,
  /**
   * Replaces the wrapper's classes rather than adding to them: the tile needs
   * `absolute` where the row needs `relative`, and two position utilities in the
   * same layer are decided by their order in the generated stylesheet, not by
   * the order they were written in. Whatever a caller passes must keep the menu
   * above the row-covering link overlay.
   */
  className = "relative z-[1] justify-self-end",
}: {
  asset: MediaAsset;
  onDelete: () => void;
  className?: string;
}) {
  const t = useT();
  const toast = useToast();
  const close = useClosePops();

  async function copyLink() {
    close();
    try {
      await navigator.clipboard.writeText(absoluteMediaUrl(asset.url));
      toast(t("common.copied"));
    } catch {
      toast(t("common.copyFailed"));
    }
  }

  return (
    // Above the row-covering link overlay, or the menu never gets the click.
    <span className={className}>
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={cn("crm-cb crm-cb-sm", open && "is-on")}
            title={t("common.more")}
            aria-label={t("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <MoreHorizontal className="size-4" />
          </button>
        )}
      >
        <button type="button" className="crm-mi" onClick={() => void copyLink()}>
          <Copy className="size-4" />
          {t("media.copyLink")}
        </button>
        <a
          className="crm-mi"
          href={asset.url}
          download={asset.originalFilename}
          target="_blank"
          rel="noreferrer noopener"
          onClick={close}
        >
          <Download className="size-4" />
          {t("media.download")}
        </a>

        <div className="crm-menu-sep" />
        <button
          type="button"
          className="crm-mi crm-mi-danger"
          onClick={() => {
            close();
            onDelete();
          }}
        >
          <Trash2 className="size-4" />
          {t("media.delete")}
        </button>
      </Pop>
    </span>
  );
}
